import { IDP_V2_ENDPOINTS } from '../data/idp-v2-endpoints';
import type { DocumentEndpointItem } from '../models/document-endpoint.model';
import type { IdpV2Endpoint } from '../models/idp-v2-endpoint.model';
import { toIdpV2Category } from '../models/idp-v2-endpoint.model';

const PARSE_SLUG = 'parse';
const API_BASE = '/api/v1/idp-v2';

export function resolveEndpointApiPath(slug: string, apiPath?: string | null): string {
  const trimmed = apiPath?.trim();
  if (trimmed) {
    return trimmed;
  }
  return `${API_BASE}/${slug}`;
}

export function isGenericEndpointDescription(description: string, title: string): boolean {
  return description.trim() === `IDP document endpoint: ${title}`;
}

export function mergeEndpointDescription(
  tenantDescription: string | null | undefined,
  title: string,
  fallback: string,
): string {
  const trimmed = tenantDescription?.trim();
  if (!trimmed || isGenericEndpointDescription(trimmed, title)) {
    return fallback;
  }
  return trimmed;
}

export function mapDocumentEndpointToIdpV2(item: DocumentEndpointItem): IdpV2Endpoint {
  const apiPath = resolveEndpointApiPath(item.apiSlug, item.apiPath);
  const staticMatch = IDP_V2_ENDPOINTS.find((endpoint) => endpoint.slug === item.apiSlug);
  const category = toIdpV2Category(item.category);

  if (staticMatch) {
    return {
      ...staticMatch,
      apiPath,
      category,
      name: item.title,
      description: mergeEndpointDescription(
        item.description,
        item.title,
        staticMatch.description,
      ),
      docTypeKey: item.documentType || staticMatch.docTypeKey,
    };
  }

  return {
    id: item.apiSlug,
    slug: item.apiSlug,
    apiPath,
    docTypeKey: item.documentType,
    name: item.title,
    description: item.description?.trim() ?? '',
    category,
    kind: item.apiSlug === PARSE_SLUG ? 'parse' : 'process',
  };
}

export function mapDocumentEndpoints(items: DocumentEndpointItem[]): IdpV2Endpoint[] {
  return items.map(mapDocumentEndpointToIdpV2);
}

export function filterIdpV2Endpoints(endpoints: IdpV2Endpoint[], query: string): IdpV2Endpoint[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return endpoints;
  }

  return endpoints.filter(
    (endpoint) =>
      endpoint.name.toLowerCase().includes(normalized) ||
      endpoint.slug.toLowerCase().includes(normalized) ||
      endpoint.description.toLowerCase().includes(normalized) ||
      (endpoint.docTypeKey?.toLowerCase().includes(normalized) ?? false),
  );
}

export function groupEndpointsByCategory(
  endpoints: IdpV2Endpoint[],
): { category: string; endpoints: IdpV2Endpoint[] }[] {
  const order: string[] = [];
  const map = new Map<string, IdpV2Endpoint[]>();

  for (const endpoint of endpoints) {
    if (!map.has(endpoint.category)) {
      map.set(endpoint.category, []);
      order.push(endpoint.category);
    }
    map.get(endpoint.category)!.push(endpoint);
  }

  return order.map((category) => ({
    category,
    endpoints: map.get(category) ?? [],
  }));
}
